import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Trash2, Plus, Minus, ArrowLeft, ArrowRight, Truck, Store } from 'lucide-react';
import { Button } from '../components/ui/button';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { isWeightItem, gramsOf, normalizeGrams, formatGrams, WEIGHT_PRESETS } from '../lib/cartUnits';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DELIVERY_FEE = 15;

function CartWeightEditor({ item, onSet }) {
    const currentGrams = gramsOf(item);
    const [custom, setCustom] = useState('');

    const commitCustom = () => {
        if (custom === '') return;
        const g = normalizeGrams(Number(custom));
        setCustom('');
        onSet(g);
    };

    return (
        <div className="mt-2 space-y-2" data-testid={`cart-weight-${item.product_id}`}>
            <div className="flex flex-wrap items-center gap-1.5">
                {WEIGHT_PRESETS.map((g) => (
                    <button
                        key={g}
                        type="button"
                        onClick={() => onSet(g)}
                        className={`text-xs font-medium px-2.5 py-1 rounded-md border transition-colors ${currentGrams === g ? 'bg-[#1B4D3E] text-white border-[#1B4D3E]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#1B4D3E]'}`}
                        data-testid={`cart-weight-preset-${g}-${item.product_id}`}
                    >
                        {formatGrams(g)}
                    </button>
                ))}
                <div className="relative w-24">
                    <input
                        type="number"
                        min="100"
                        step="50"
                        placeholder="grams"
                        value={custom}
                        onChange={(e) => setCustom(e.target.value)}
                        onBlur={commitCustom}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitCustom(); } }}
                        className="w-full text-sm border border-slate-200 rounded-md pl-2 pr-5 py-1 focus:outline-none focus:border-[#1B4D3E]"
                        data-testid={`cart-weight-custom-${item.product_id}`}
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">g</span>
                </div>
            </div>
            <p className="text-xs text-slate-400">
                Currently {formatGrams(currentGrams)} · min 100g · steps of 50g
            </p>
        </div>
    );
}

export default function CartPage() {
    const navigate = useNavigate();
    const { cart, updateQuantity, removeFromCart, clearCart } = useCart();
    const { isAuthenticated } = useAuth();
    const [deliveryType, setDeliveryType] = useState('delivery');
    const [loading, setLoading] = useState(false);

    const handleQuantityChange = async (productId, delta) => {
        const item = cart.items.find(i => i.product_id === productId);
        if (!item) return;
        
        const newQty = Math.max(0, item.quantity + delta);
        try {
            if (newQty === 0) {
                await removeFromCart(productId);
                toast.success('Item removed');
            } else {
                await updateQuantity(productId, newQty);
            }
        } catch (error) {
            toast.error('Failed to update cart');
        }
    };

    const handleSetWeight = async (productId, grams) => {
        try {
            if (!grams || grams <= 0) {
                await removeFromCart(productId);
                toast.success('Item removed');
            } else {
                await updateQuantity(productId, grams / 1000);
            }
        } catch (error) {
            toast.error('Failed to update cart');
        }
    };

    const handleProceedToCheckout = async () => {
        if (cart.items.length === 0) {
            toast.error('Your cart is empty');
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post(
                `${API}/orders`,
                { delivery_type: deliveryType },
                { withCredentials: true }
            );
            
            navigate(`/checkout/${response.data.order_id}`);
        } catch (error) {
            toast.error('Failed to create order');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    if (cart.items.length === 0) {
        return (
            <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
                <div className="text-center px-4" data-testid="empty-cart">
                    <div className="w-24 h-24 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-6">
                        <ShoppingCart className="w-12 h-12 text-slate-400" />
                    </div>
                    <h1 className="text-2xl font-bold text-slate-900 mb-2">Your cart is empty</h1>
                    <p className="text-slate-500 mb-6">Add some fresh produce to get started</p>
                    <Link to="/shop">
                        <Button className="btn-primary">
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Continue Shopping
                        </Button>
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F8F9FA] pb-8">
            <div className="container-main py-6 md:py-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Your Cart</h1>
                        <p className="text-slate-500">{cart.item_count} items</p>
                    </div>
                    <Button 
                        variant="ghost" 
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        onClick={() => {
                            clearCart();
                            toast.success('Cart cleared');
                        }}
                        data-testid="clear-cart-btn"
                    >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Clear Cart
                    </Button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Cart Items */}
                    <div className="lg:col-span-2 space-y-4">
                        {cart.items.map((item) => (
                            <div 
                                key={item.product_id} 
                                className="bg-white rounded-xl p-4 shadow-sm border border-slate-100"
                                data-testid={`cart-item-${item.product_id}`}
                            >
                                <div className="flex gap-4">
                                    <div className="w-20 h-20 rounded-lg bg-slate-100 flex-shrink-0 overflow-hidden">
                                        {item.image_url ? (
                                            <img 
                                                src={item.image_url} 
                                                alt={item.name}
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = '';
                                                    e.target.parentElement.innerHTML = '<span class="text-3xl flex items-center justify-center h-full">🥬</span>';
                                                }}
                                            />
                                        ) : (
                                            <span className="text-3xl flex items-center justify-center h-full">🥬</span>
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-semibold text-slate-900 truncate">
                                            {item.name}
                                        </h3>
                                        <p className="text-sm text-slate-500">
                                            per {item.unit}
                                        </p>
                                        {item.price != null && (
                                            <p className="text-[#1B4D3E] font-semibold mt-1">
                                                ${item.price.toFixed(2)} {isWeightItem(item) ? '/ kg' : 'each'}
                                            </p>
                                        )}
                                        {isWeightItem(item) ? (
                                            <div className="mt-3">
                                                <CartWeightEditor
                                                    item={item}
                                                    onSet={(g) => handleSetWeight(item.product_id, g)}
                                                />
                                                <div className="flex items-center justify-between mt-2">
                                                    <button
                                                        onClick={() => handleSetWeight(item.product_id, 0)}
                                                        className="inline-flex items-center text-sm text-red-500 hover:text-red-600"
                                                        data-testid={`cart-remove-${item.product_id}`}
                                                    >
                                                        <Trash2 className="w-4 h-4 mr-1" /> Remove
                                                    </button>
                                                    {item.price != null && (
                                                        <span className="font-bold text-slate-900" data-testid={`cart-line-total-${item.product_id}`}>
                                                            ${(item.price * item.quantity).toFixed(2)}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex items-center justify-between mt-3">
                                                <div className="quantity-stepper">
                                                    <button
                                                        onClick={() => handleQuantityChange(item.product_id, -1)}
                                                        className="quick-add-btn bg-white text-slate-700 hover:bg-slate-50 w-8 h-8"
                                                        data-testid={`cart-remove-${item.product_id}`}
                                                    >
                                                        <Minus className="w-4 h-4" />
                                                    </button>
                                                    <span className="w-10 text-center font-semibold">
                                                        {item.quantity}
                                                    </span>
                                                    <button
                                                        onClick={() => handleQuantityChange(item.product_id, 1)}
                                                        className="quick-add-btn bg-orange-500 text-white hover:bg-orange-600 w-8 h-8"
                                                        data-testid={`cart-add-${item.product_id}`}
                                                    >
                                                        <Plus className="w-4 h-4" />
                                                    </button>
                                                </div>
                                                {item.price != null && (
                                                    <div className="text-right">
                                                        <span className="font-bold text-slate-900">
                                                            ${(item.price * item.quantity).toFixed(2)}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}

                        <Link to="/shop" className="inline-flex items-center text-[#1B4D3E] hover:underline">
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Continue Shopping
                        </Link>
                    </div>

                    {/* Order Summary */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 sticky top-24">
                            <h2 className="text-xl font-bold text-slate-900 mb-6">Order Summary</h2>

                            {/* Delivery Type */}
                            <div className="mb-6">
                                <Label className="text-sm font-medium text-slate-700 mb-3 block">
                                    Fulfillment Method
                                </Label>
                                <RadioGroup 
                                    value={deliveryType} 
                                    onValueChange={setDeliveryType}
                                    className="space-y-3"
                                >
                                    <div className="flex items-center space-x-3 p-3 rounded-lg border border-slate-200 hover:border-[#1B4D3E] cursor-pointer transition-colors">
                                        <RadioGroupItem value="delivery" id="delivery" data-testid="delivery-option" />
                                        <Label htmlFor="delivery" className="flex items-center gap-2 cursor-pointer flex-1">
                                            <Truck className="w-5 h-5 text-[#1B4D3E]" />
                                            <span>Delivery</span>
                                        </Label>
                                    </div>
                                    <div className="flex items-center space-x-3 p-3 rounded-lg border border-slate-200 hover:border-[#1B4D3E] cursor-pointer transition-colors">
                                        <RadioGroupItem value="pickup" id="pickup" data-testid="pickup-option" />
                                        <Label htmlFor="pickup" className="flex items-center gap-2 cursor-pointer flex-1">
                                            <Store className="w-5 h-5 text-[#1B4D3E]" />
                                            <span>Pickup</span>
                                        </Label>
                                    </div>
                                </RadioGroup>
                            </div>

                            {/* Order Info */}
                            <div className="space-y-3 border-t pt-4">
                                <div className="flex justify-between text-slate-600">
                                    <span>Items</span>
                                    <span>{cart.item_count} items</span>
                                </div>
                                <div className="flex justify-between text-slate-600">
                                    <span>Subtotal</span>
                                    <span className="font-semibold text-slate-900">
                                        ${(cart.subtotal || 0).toFixed(2)}
                                    </span>
                                </div>
                                {deliveryType === 'delivery' ? (
                                    <div className="flex justify-between text-slate-600" data-testid="cart-delivery-fee">
                                        <span>Delivery fee</span>
                                        <span className="font-medium text-slate-900">${DELIVERY_FEE.toFixed(2)}</span>
                                    </div>
                                ) : (
                                    <div className="flex justify-between text-slate-600" data-testid="cart-pickup">
                                        <span>Pickup</span>
                                        <span className="text-[#1B4D3E] font-medium">Orange Grove Market</span>
                                    </div>
                                )}
                                <div className="flex justify-between text-lg font-bold text-slate-900 border-t pt-3">
                                    <span>Total</span>
                                    <span className="text-[#1B4D3E]" data-testid="cart-total">
                                        ${((cart.subtotal || 0) + (deliveryType === 'delivery' ? DELIVERY_FEE : 0)).toFixed(2)}
                                    </span>
                                </div>
                            </div>

                            <Button 
                                className="btn-primary w-full mt-6 py-4 h-auto text-lg"
                                onClick={handleProceedToCheckout}
                                disabled={loading}
                                data-testid="proceed-checkout-btn"
                            >
                                {loading ? (
                                    <span className="spinner" />
                                ) : (
                                    <>
                                        Proceed to Checkout
                                        <ArrowRight className="w-5 h-5 ml-2" />
                                    </>
                                )}
                            </Button>

                            {!isAuthenticated && (
                                <p className="text-sm text-slate-500 text-center mt-4">
                                    <Link to="/login" className="text-[#1B4D3E] hover:underline">
                                        Login
                                    </Link>
                                    {' '}for faster checkout & order history
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
